import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarHeartIcon, PlusIcon } from 'lucide-react';
import {
  Button,
  DataTable,
  Drawer,
  EmptyState,
  ErrorNotice,
  Field,
  MultiCombobox,
  PageHeading,
  Pagination,
  Panel,
  Pill,
  SearchInput,
  SkeletonTable,
  TextArea,
  TextInput,
  useClientTable,
  type DataTableSort,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { readMyIdentity } from '@/lib/events';
import {
  MONTH_OPTIONS,
  SEASON_TAGS_QUERY_KEY,
  canEditSeasonTags,
  createSeasonTag,
  listSeasonTags,
  listTaggedPrograms,
  updateSeasonTag,
  type SeasonTag,
  type SeasonTagDraft,
} from '@/lib/season-tags';

const NAME_MAX = 60;
const DESCRIPTION_MAX = 240;

const SORT_FIELDS = {
  name: 'name',
  months: 'firstMonth',
  programs: 'programCount',
  status: 'statusLabel',
} as const;
const SORT_KEYS = Object.values(SORT_FIELDS);
/** The calendar's order: January's seasons first. */
const DEFAULT_SORT: DataTableSort = { key: SORT_FIELDS.months, direction: 'asc' };

const STATUS_LABEL = { active: 'In use', retired: 'Retired' } as const;

/** Which drawer is open: none, a new tag, or one tag by id. */
type DrawerState = { readonly kind: 'closed' } | { readonly kind: 'new' } | { readonly kind: 'tag'; readonly id: string };
const CLOSED: DrawerState = { kind: 'closed' };

/**
 * Catalog → Season tags (Roy's portal list, item 7): the seasons and occasions
 * programs can be tagged for, each with its months. The client portal's
 * calendar shows the tagged programs as "Ideas for …" in those months.
 * Anyone on staff can read the list; changing it follows program editing.
 *
 * @returns The page.
 */
export function SeasonTagsPage(): React.ReactElement {
  const [drawer, setDrawer] = useState<DrawerState>(CLOSED);
  const identity = useQuery({ queryKey: ['my-identity'], queryFn: ({ signal }) => readMyIdentity(signal), staleTime: 5 * 60_000 });
  const canEdit = canEditSeasonTags(identity.data?.role);

  const tags = useQuery({ queryKey: SEASON_TAGS_QUERY_KEY, queryFn: ({ signal }) => listSeasonTags(signal) });
  const rows = (tags.data ?? []).map((tag) => ({
    ...tag,
    firstMonth: tag.months[0] ?? 13,
    statusLabel: tag.isActive ? STATUS_LABEL.active : STATUS_LABEL.retired,
  }));
  const table = useClientTable({
    rows,
    searchText: (row) => `${row.name} ${row.monthsLabel} ${row.description ?? ''}`,
    sortKeys: SORT_KEYS,
    defaultSort: DEFAULT_SORT,
  });
  const open = drawer.kind === 'tag' ? (tags.data ?? []).find((tag) => tag._id === drawer.id) ?? null : null;

  return (
    <section className="animate-portal-rise">
      <PageHeading
        breadcrumbs={[{ label: 'Catalog' }, { label: 'Season tags' }]}
        title="Season tags"
        subtitle="Seasons and occasions to tag programs for. Clients see the tagged programs as ideas on their calendar in those months."
        action={
          canEdit ? (
            <Button guide="season-tags-add" onClick={() => setDrawer({ kind: 'new' })}>
              <PlusIcon aria-hidden className="size-4" /> Add tag
            </Button>
          ) : undefined
        }
      />

      <div className="mb-4">
        <SearchInput
          data-guide="season-tags-search"
          label="Search season tags"
          placeholder="Tag or month"
          value={table.search}
          onChange={(event) => table.setSearch(event.target.value)}
          className="w-full max-w-sm"
        />
      </div>

      {tags.isPending ? (
        <SkeletonTable label="Loading season tags…" />
      ) : tags.isError ? (
        <ErrorNotice title="Season tags unavailable" message={errorMessage(tags.error)} />
      ) : table.matched === 0 ? (
        <Panel>
          {table.search ? (
            <EmptyState title="No tags match" message="Try a different name or month." />
          ) : (
            <EmptyState
              icon={<CalendarHeartIcon aria-hidden className="size-6" />}
              title="No season tags yet"
              message="Add a season or occasion, like National Volunteer Month in April, then tag the programs that suit it."
              {...(canEdit ? { action: <Button onClick={() => setDrawer({ kind: 'new' })}>Add tag</Button> } : {})}
            />
          )}
        </Panel>
      ) : (
        <>
          <DataTable
            guide="season-tags-table"
            headers={[
              { label: 'Tag', sortKey: SORT_FIELDS.name },
              { label: 'Months', sortKey: SORT_FIELDS.months },
              { label: 'Programs', sortKey: SORT_FIELDS.programs },
              { label: 'Status', sortKey: SORT_FIELDS.status },
            ]}
            rightAlignedHeaders={[2]}
            {...(table.sort === undefined ? {} : { sort: table.sort })}
            onSort={table.onSort}
          >
            {table.pageRows.map((tag) => (
              <tr key={tag._id} className="border-t border-portal-line align-middle">
                <td className="p-3 text-portal-small">
                  <button
                    type="button"
                    onClick={() => setDrawer({ kind: 'tag', id: tag._id })}
                    className="text-left font-bold text-brand underline-offset-2 hover:underline"
                  >
                    {tag.name}
                  </button>
                  {tag.description ? (
                    <span className="block text-portal-micro text-portal-muted">{tag.description}</span>
                  ) : null}
                </td>
                <td className="p-3 text-portal-small whitespace-nowrap">{tag.monthsLabel}</td>
                <td className="p-3 text-right text-portal-small whitespace-nowrap">{tag.programCount.toLocaleString('en-US')}</td>
                <td className="p-3 text-portal-small whitespace-nowrap">
                  {/* Pills mark exceptions: nearly every tag is in use. */}
                  {tag.isActive ? <span className="text-portal-muted">{tag.statusLabel}</span> : <Pill tone="neutral">{tag.statusLabel}</Pill>}
                </td>
              </tr>
            ))}
          </DataTable>
          <Pagination page={table.page} totalPages={table.totalPages} onChange={table.setPage} summary={table.summary} className="mt-3" />
        </>
      )}

      {drawer.kind === 'new' ? <NewTagDrawer onClose={() => setDrawer(CLOSED)} /> : null}
      {open ? <TagDrawer key={open._id} tag={open} canEdit={canEdit} onClose={() => setDrawer(CLOSED)} /> : null}
    </section>
  );
}

/** Months picked in the combobox, as the API takes them. */
function monthsFrom(values: readonly string[]): readonly number[] {
  return values.map(Number).sort((a, b) => a - b);
}

/**
 * The fields a tag is made of. Shared by the add and edit drawers so the two
 * cannot drift apart.
 */
function TagFields({
  draft,
  onChange,
  idPrefix,
}: {
  readonly draft: SeasonTagDraft;
  readonly onChange: (draft: SeasonTagDraft) => void;
  readonly idPrefix: string;
}): React.ReactElement {
  return (
    <div className="space-y-4">
      <Field guide="season-tag-name" label="Name" htmlFor={`${idPrefix}-name`} required hint="As clients read it: “Ideas for National Volunteer Month”.">
        <TextInput
          id={`${idPrefix}-name`}
          value={draft.name}
          maxLength={NAME_MAX}
          placeholder="National Volunteer Month"
          onChange={(event) => onChange({ ...draft, name: event.target.value })}
        />
      </Field>
      <Field guide="season-tag-months" label="Months" htmlFor={`${idPrefix}-months`} required hint="The calendar shows the ideas in these months.">
        <MultiCombobox
          id={`${idPrefix}-months`}
          options={MONTH_OPTIONS}
          values={draft.months.map(String)}
          placeholder="Pick months"
          onChange={(values) => onChange({ ...draft, months: monthsFrom(values) })}
        />
      </Field>
      <Field guide="season-tag-why" label="Why this season" htmlFor={`${idPrefix}-description`} hint="One line clients read under the season on their calendar.">
        <TextArea
          id={`${idPrefix}-description`}
          rows={3}
          maxLength={DESCRIPTION_MAX}
          value={draft.description}
          onChange={(event) => onChange({ ...draft, description: event.target.value })}
        />
      </Field>
    </div>
  );
}

function isComplete(draft: SeasonTagDraft): boolean {
  return draft.name.trim().length > 0 && draft.months.length > 0;
}

/** Adds a tag; the drawer closes on save and the list shows it. */
function NewTagDrawer({ onClose }: { readonly onClose: () => void }): React.ReactElement {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<SeasonTagDraft>({ name: '', months: [], description: '' });
  const create = useMutation({
    mutationFn: () => createSeasonTag(draft),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: SEASON_TAGS_QUERY_KEY });
      onClose();
    },
  });

  return (
    <Drawer
      open
      title="Add a season tag"
      onClose={() => (create.isPending ? undefined : onClose())}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button guide="season-tag-submit" onClick={() => create.mutate()} disabled={!isComplete(draft) || create.isPending}>
            {create.isPending ? 'Adding…' : 'Add tag'}
          </Button>
        </div>
      }
    >
      <TagFields draft={draft} onChange={setDraft} idPrefix="new-tag" />
      {create.isError ? (
        <div className="mt-4">
          <ErrorNotice inline title="Not added" message={errorMessage(create.error)} />
        </div>
      ) : null}
    </Drawer>
  );
}

/**
 * One tag: its programs, and for editors its fields plus retire or restore.
 * Retiring keeps the tag on its programs but out of the portal and the picker.
 */
function TagDrawer({
  tag,
  canEdit,
  onClose,
}: {
  readonly tag: SeasonTag;
  readonly canEdit: boolean;
  readonly onClose: () => void;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<SeasonTagDraft>({ name: tag.name, months: tag.months, description: tag.description ?? '' });
  const programs = useQuery({
    queryKey: [...SEASON_TAGS_QUERY_KEY, tag._id, 'programs'],
    queryFn: ({ signal }) => listTaggedPrograms(tag._id, signal),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: SEASON_TAGS_QUERY_KEY });
  const save = useMutation({
    mutationFn: () => updateSeasonTag(tag._id, draft),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  const toggle = useMutation({
    mutationFn: () => updateSeasonTag(tag._id, { isActive: !tag.isActive }),
    onSuccess: refresh,
  });
  const busy = save.isPending || toggle.isPending;

  return (
    <Drawer
      open
      title={tag.name}
      subtitle={`${tag.monthsLabel}${tag.isActive ? '' : ' · Retired'}`}
      onClose={() => (busy ? undefined : onClose())}
      {...(canEdit
        ? {
            footer: (
              <div className="flex items-center justify-between gap-2">
                <Button variant="secondary" onClick={() => toggle.mutate()} disabled={busy}>
                  {tag.isActive ? 'Retire tag' : 'Restore tag'}
                </Button>
                <div className="flex gap-2">
                  <Button variant="secondary" onClick={onClose} disabled={busy}>
                    Cancel
                  </Button>
                  <Button onClick={() => save.mutate()} disabled={!isComplete(draft) || busy}>
                    {save.isPending ? 'Saving…' : 'Save'}
                  </Button>
                </div>
              </div>
            ),
          }
        : {})}
    >
      <div className="space-y-6">
        {canEdit ? (
          <TagFields draft={draft} onChange={setDraft} idPrefix="edit-tag" />
        ) : tag.description ? (
          <p className="rounded-portal-button bg-portal-bg p-3.5 text-portal-small leading-[1.6]">{tag.description}</p>
        ) : null}

        {!tag.isActive ? (
          <p className="text-portal-small text-portal-muted">
            Retired: it stays on its programs but clients no longer see it, and it is left out of the program picker.
          </p>
        ) : null}

        <div>
          <p className="text-portal-small font-bold">Tagged programs</p>
          {programs.isPending ? (
            <p className="mt-2 text-portal-small text-portal-muted">Loading programs…</p>
          ) : programs.isError ? (
            <ErrorNotice inline title="Programs unavailable" message={errorMessage(programs.error)} />
          ) : programs.data.length === 0 ? (
            <EmptyState
              icon={<CalendarHeartIcon aria-hidden className="size-6" />}
              title="No programs yet"
              message="Tag programs for this season from their page under Catalog → Programs."
            />
          ) : (
            <ul className="mt-2 divide-y divide-portal-line rounded-portal-button border border-portal-line">
              {programs.data.map((program) => (
                <li key={program.id}>
                  <Link to={`/programs/${program.id}`} className="block p-3 text-portal-small text-brand hover:bg-portal-bg">
                    {program.name}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        {save.isError || toggle.isError ? (
          <ErrorNotice inline title="Not saved" message={errorMessage(save.error ?? toggle.error)} />
        ) : null}
      </div>
    </Drawer>
  );
}
